import { Box, Stack, Typography } from '@mui/material';
import type { NodeOutput } from '../../../types/flow-execution.types';
import Section from './Section';
import { resolveEdgeField } from './resolveEdgeField';

interface Props {
  data: NodeOutput;
}

const INPUT_FIELDS: Array<{
  label: string;
  plainKey: string;
  dataKey: string;
}> = [
  { label: 'Prompt', plainKey: 'prompt', dataKey: 'promptData' },
  { label: 'Content', plainKey: 'content', dataKey: 'contentData' },
  { label: 'Persona', plainKey: 'persona', dataKey: 'personaData' },
  { label: 'Instruction', plainKey: 'instruction', dataKey: 'instructionData' },
];

function TextGeneratorPanel({ data }: Props) {
  const input = (data.input ?? {}) as Record<string, unknown>;
  const output = (data.outputData ?? {}) as Record<string, unknown>;
  const aiModel =
    (output.aiModel as string | undefined) ??
    (input.aiModel as string | undefined);
  const outputText =
    (output.text as string | undefined) ?? data.output ?? undefined;
  const errorMessage = output.errorMessage as string | undefined;
  const resolvedInputs = INPUT_FIELDS.map(field => ({
    label: field.label,
    value: resolveEdgeField(input[field.plainKey], input[field.dataKey]),
  })).filter(field => field.value.length > 0);
  return (
    <Stack spacing={1.5}>
      <Section title='Model'>
        {aiModel ? (
          aiModel
        ) : (
          <Typography variant='caption' color='text.secondary'>
            No model recorded.
          </Typography>
        )}
      </Section>
      <Section title='Input'>
        {resolvedInputs.length === 0 ? (
          <Typography variant='caption' color='text.secondary'>
            No input provided.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {resolvedInputs.map(field => (
              <Box key={field.label}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}
                >
                  {field.label}
                </Typography>
                <Box>{field.value}</Box>
              </Box>
            ))}
          </Stack>
        )}
      </Section>
      <Section title='Output'>
        {data.isError ? (
          <Typography variant='body2' color='error'>
            {errorMessage || 'Node execution failed.'}
          </Typography>
        ) : outputText ? (
          outputText
        ) : (
          <Typography variant='caption' color='text.secondary'>
            No output produced.
          </Typography>
        )}
      </Section>
    </Stack>
  );
}

export default TextGeneratorPanel;
